import React from 'react';
import _ from 'lodash';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import { GetApi } from '../../../../../api/api';
import {
  CustomFeatureType,
  FeatureTypeGroup,
} from '../../../../../api/types';
import { Feedback } from "../../../../../components/Validator";
import AppContext from "../../../../../components/AppContext";
import withModal, { WithModalProps, InjectedProps } from "../../../../../components/HOC/WithModal";
import StepForm, { Step } from '../../../../../components/StepForm';
import Pending from '../../../../../components/Pending';

import AssignToFeatureTypeGroups from './AssignToFeatureTypeGroups';
import FeatureTypeValidator from './FeatureTypeValidator';


const nameId = 'nameInputFeatureType';
const descriptionId = 'descriptionInputFeatureType';

interface InnerEditProps extends EditProps {
  featureType: CustomFeatureType
  featureTypeGroups: FeatureTypeGroup[]
}

interface InnerEditState {
  featureType: CustomFeatureType
  featureTypeGroups: FeatureTypeGroup[]
  defaultGroup: string
  valid: Feedback
  disabled: boolean
}

class InnerEdit extends React.Component<InnerEditProps, InnerEditState> {
  public static contextType = AppContext;

  constructor(props: InnerEditProps) {
    super(props);
    this.state = {
      featureType: props.featureType,
      featureTypeGroups: props.featureTypeGroups,
      defaultGroup: props.featureType.groups?.find(g => g.primary)?.groupId.toString() || '',
      valid: props.valid,
      disabled: props.disabled
    }
  }

  public componentDidUpdate( prevProps: InnerEditProps ) {
    if (!_.isEqual(prevProps, this.props)) {
      this.setState({
        valid: this.props.valid,
        disabled: this.props.disabled
      })
    }
  }

  public render() {
    return (
      <StepForm>
        <Step name="Basic information">{this.renderBasicInfo()}</Step>
        <Step name="Assign feature type groups">{this.renderAssignFtGroups()}</Step>
      </StepForm>
    );
  }

  public renderBasicInfo = () => {
    const disabledName = this.state.disabled || (!this.state.valid.valid && this.state.valid.id !== nameId)
    const disabledDescription = this.state.disabled || (!this.state.valid.valid && this.state.valid.id !== descriptionId)

    const inValidName = this.state.valid.id === nameId;
    const inValidDescription = this.state.valid.id === descriptionId;

    return (
      <Form>
        <Form.Group as={Row} controlId={nameId}>
          <Form.Label column={true} md='3'>
            Name{' '}<span className="text-danger">*</span>
          </Form.Label>
          <Col md='9'>
            <Form.Control
              type='text'
              value={this.state.featureType.name}
              onChange={this.setName}
              onBlur={this.updateCustomFeatureType}
              disabled={disabledName}
              isInvalid={inValidName}
            />
            <Form.Control.Feedback type='invalid'>
              {this.state.valid.message}
            </Form.Control.Feedback>
            <Form.Text>
              Name of the new feature type.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId={descriptionId}>
          <Form.Label column={true} md='3'>Description</Form.Label>
          <Col md='9'>
            <Form.Control
              type='text'
              value={this.state.featureType.description!}
              onChange={this.setDescription}
              onBlur={this.updateCustomFeatureType}
              disabled={disabledDescription}
              isInvalid={inValidDescription}
            />
            <Form.Control.Feedback type='invalid'>
              {this.state.valid.message}
            </Form.Control.Feedback>
            <Form.Text>
              An optional description for the new feature type.
            </Form.Text>
          </Col>
        </Form.Group>
      </Form>
    );
  }
  
  public renderAssignFtGroups = () => (
    <Form>
      <AssignToFeatureTypeGroups
        default={this.state.defaultGroup}
        groups={this.state.featureType.groups!}
        featureTypeGroups={this.state.featureTypeGroups}
        currentVersion={this.props.currentVersion}
        setGroups={this.setGroups}
        setDefault={this.setDefaultGroup}
        onRemove={this.removeGroup}
        valid={this.state.valid}
        disabled={this.state.disabled}
      />
    </Form>
  );
  
  private updateCustomFeatureType = () => {
    if (this.state.valid.valid) {
      this.setDisabled(true);
      GetApi()
      .then(api => api.updateCustomFeatureType(
        this.props.fccId,
        this.state.featureType.id,
        this.state.featureType
      ))
      .catch(error => this.context.useToasts.addToast(error, 'danger'))
      .finally(() => this.setDisabled(false))
    }
  }

  private setName = ( e: any ) => {
    e.persist();
    const isValid = FeatureTypeValidator.getFieldValidator('name')(nameId, e.target.value)
    this.setState(prevState => ({
      featureType: {
        ...prevState.featureType,
        name: e.target.value,
      },
      valid: isValid
    }), () => this.props.setValid(isValid))
  }

  private setDescription = ( e: any ) => {
    e.persist();
    const isValid = FeatureTypeValidator.getFieldValidator('description')(descriptionId, e.target.value)
    this.setState(prevState => ({
      featureType: {
        ...prevState.featureType,
        description: e.target.value,
      },
      valid: isValid
    }), () => this.props.setValid(isValid))
  }
  
  private setGroups = ( e: any ) => {
    e.persist();
    this.setState(prevState => ({
      defaultGroup: prevState.featureType.groups!.length === 0
        ? e.target.value.toString()
        : prevState.defaultGroup,
      featureType: {
        ...prevState.featureType,
        groups: [
          ...prevState.featureType.groups!,
          {
            groupId: parseInt(e.target.value, 10),
            primary: prevState.featureType.groups!.length === 0
              ? true
              : false
          }
        ]
      }
    }), () => this.updateCustomFeatureType())
  }
  
  private removeGroup = ( id: number ) => {
    const newDefault = id.toString() === this.state.defaultGroup;
    const newGroups = this.state.featureType.groups!.filter(g => g.groupId !== id);
    this.setState(prevState => ({
      defaultGroup: newDefault
        ? (newGroups.length > 0 ? newGroups[0].groupId.toString() : '')
        : prevState.defaultGroup,
      featureType: {
        ...prevState.featureType,
        groups: newGroups.length > 0 ? newGroups.map((g, i) => ({
          ...g,
          primary: newDefault
            ? (i === 0 ? true : false)
            : g.primary
        })) : []
      }
    }), () => this.updateCustomFeatureType())
  }
  
  private setDefaultGroup = ( e: any ) => {
    e.persist();
    this.setState(prevState => ({
      defaultGroup: e.target.value.toString(),
      featureType: {
        ...prevState.featureType,
        groups: prevState.featureType.groups!.map(g => ({
            ...g,
            primary: g.groupId === parseInt(e.target.value, 10)
          })
        )
      },
    }), () => this.updateCustomFeatureType())
  }

  private setDisabled = ( value: boolean ) => {
    this.props.setDisabled(value);
    this.setState({
      disabled: value
    });
  }
}

interface EditProps extends InjectedProps {
  cftId: number
  fccId: string
  bfcId: string
  currentVersion: number
}

const Edit = ( props: EditProps ) => {
  const getData = async (): Promise<InnerEditProps> => {
    const api = await GetApi();
    const [ featureType, featureTypeGroups ] = await Promise.all([
      api.getCustomFeatureType(props.fccId, props.cftId),
      api.getFeatureTypeGroups(props.bfcId)
    ])
    return {
      ...props,
      featureType: {
        ...featureType,
        description: featureType.description || '',
        groups: featureType.groups || [],
      },
      featureTypeGroups,
    }
  }

  const renderSuccess = ( innerProps: InnerEditProps ) => <InnerEdit {...innerProps} />;

  return (
    <Pending
      getData={getData}
      renderSuccess={renderSuccess}
    />
  );
}

export interface EditModalProps extends WithModalProps {
  cftId: number
  fccId: string
  bfcId: string
  currentVersion: number
}

const EditModal = withModal(Edit);

export default EditModal;